import type { PlanPayload, PlansListResponse } from '@/types';

import { atom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';

import { apiClient } from '@/utils/api';
import { buildApiParams } from '@/utils/helpers';
import { createPaginationParams, createTableQueryOptions } from '@/components/table';

const PLANS_BASE = '/plans';

export type PlansListParams = {
  page: number;
  per_page: number;
  filters: Record<string, string | number | boolean | undefined>;
  sort?: string;
  refresh?: number;
};

export const plansListParamsAtom = atom<PlansListParams>({
  ...createPaginationParams(),
  filters: {}
});

export const plansHydratedAtom = atom<boolean>(false);

export const plansQueryAtom = atomWithQuery<PlansListResponse>((get) => {
  const params = get(plansListParamsAtom);
  const hydrated = get(plansHydratedAtom);

  return createTableQueryOptions({
    queryKey: ['plans', params] as const,
    queryFn: async () => {
      const apiParams = buildApiParams(
        {
          page: params.page,
          per_page: params.per_page,
          sort: params.sort
        },
        params.filters || {}
      );

      const { data } = await apiClient.get(PLANS_BASE, { params: apiParams });
      return data as PlansListResponse;
    },
    enabled: hydrated
  });
});

const touchPlansList = (get: any, set: any) => {
  const currentParams = get(plansListParamsAtom);
  set(plansListParamsAtom, { ...currentParams, refresh: Date.now() });
};

export const createPlan = atom(null, async (get, set, payload: PlanPayload) => {
  await apiClient.post(PLANS_BASE, payload);
  touchPlansList(get, set);
});

export const updatePlan = atom(
  null,
  async (get, set, { id, payload }: { id: number; payload: PlanPayload }) => {
    await apiClient.put(`${PLANS_BASE}/${id}`, payload);
    touchPlansList(get, set);
  }
);

export const deletePlan = atom(null, async (get, set, id: number) => {
  await apiClient.delete(`${PLANS_BASE}/${id}`);
  touchPlansList(get, set);
});
